import { graphMailService, type SendMailResult, type VersandAntwort } from "./graphMailService";

/**
 * T-340 — „Ist die Mail raus?“ verlässlich beantworten.
 *
 * Der Server sendet mit Kennung (`sendeId`) im Hintergrund und antwortet nach
 * den Prüfungen sofort — entweder schon mit dem Ergebnis oder mit „läuft“.
 * Reißt die Verbindung (Proxy-504, eigene Zeitüberschreitung), ist der Ausgang
 * trotzdem erfragbar: `warteAufVersand` fragt den Status ab, bis er feststeht.
 * Eine Statusabfrage sendet NIE selbst.
 */

export type VersandAusgang =
    | { ausgang: "gesendet"; ergebnis: SendMailResult }
    | { ausgang: "laeuft" }
    | { ausgang: "fehlgeschlagen"; fehler: { status: number; message: string } }
    | { ausgang: "unbekannt" };

/**
 * Antwort des Servers (POST oder Status) → Ausgang. Eine erfolgreiche
 * (2xx-)Antwort OHNE `status` stammt von einem Server ohne Hintergrundlauf und
 * heißt wie bisher: gesendet.
 */
export function ausgangVon(antwort: VersandAntwort | null | undefined): VersandAusgang {
    const status =
        antwort && typeof antwort === "object" && "status" in antwort ? antwort.status : undefined;
    switch (status) {
        case "laeuft":
            return { ausgang: "laeuft" };
        case "fehlgeschlagen":
            return {
                ausgang: "fehlgeschlagen",
                fehler: (antwort as { fehler: { status: number; message: string } }).fehler,
            };
        case "unbekannt":
            return { ausgang: "unbekannt" };
        default:
            return { ausgang: "gesendet", ergebnis: (antwort ?? { ok: true }) as SendMailResult };
    }
}

/** Eine Kennung je Verfassen-Fenster. */
export function neueSendeId(): string {
    const c = globalThis.crypto as Crypto | undefined;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
    // Rückfall für Umgebungen ohne `randomUUID` (UUID v4 aus Zufallsbytes).
    const b = new Uint8Array(16);
    if (c && typeof c.getRandomValues === "function") c.getRandomValues(b);
    else for (let i = 0; i < 16; i += 1) b[i] = Math.floor(Math.random() * 256);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export interface WarteOptionen {
    /** Abstand zwischen zwei Abfragen. */
    intervallMs?: number;
    /** Spätestens dann „unbekannt“ — der Nutzer kann erneut prüfen lassen. */
    maxMs?: number;
    /**
     * So oft muss der Server hintereinander „unbekannt“ melden, bevor es dabei
     * bleibt. Puffer für den Fall, dass die erste Abfrage den Server vor dem
     * eigentlichen Versand-Request erreicht.
     */
    unbekanntNachVersuchen?: number;
    /** true → Abfragen beenden (Fenster geschlossen). */
    istAbgebrochen?: () => boolean;
    /** Für Tests austauschbar. */
    holeStatus?: (sendeId: string, mailbox?: string) => Promise<VersandAntwort>;
    schlafe?: (ms: number) => Promise<void>;
    jetzt?: () => number;
}

const standardSchlafe = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Fragt den Ausgang ab, bis er feststeht (`gesendet` | `fehlgeschlagen`) oder
 * die Zeit um ist (`unbekannt`). Netzfehler einzelner Abfragen werden
 * übergangen — gerade wenn der Proxy hakt, soll die nächste Abfrage zählen.
 */
export async function warteAufVersand(
    sendeId: string,
    mailbox: string | undefined,
    opts: WarteOptionen = {},
): Promise<Exclude<VersandAusgang, { ausgang: "laeuft" }>> {
    const intervall = opts.intervallMs ?? 1_500;
    const maxMs = opts.maxMs ?? 180_000;
    const unbekanntGrenze = Math.max(1, opts.unbekanntNachVersuchen ?? 3);
    const holeStatus = opts.holeStatus ?? graphMailService.holeVersandStatus;
    const schlafe = opts.schlafe ?? standardSchlafe;
    const jetzt = opts.jetzt ?? Date.now;
    const start = jetzt();
    let unbekanntInFolge = 0;

    for (;;) {
        if (opts.istAbgebrochen?.()) return { ausgang: "unbekannt" };
        let ausgang: VersandAusgang | null = null;
        try {
            ausgang = ausgangVon(await holeStatus(sendeId, mailbox));
        } catch {
            ausgang = null;
        }
        if (ausgang?.ausgang === "gesendet" || ausgang?.ausgang === "fehlgeschlagen") return ausgang;
        if (ausgang?.ausgang === "unbekannt") {
            unbekanntInFolge += 1;
            if (unbekanntInFolge >= unbekanntGrenze) return { ausgang: "unbekannt" };
        } else if (ausgang?.ausgang === "laeuft") {
            unbekanntInFolge = 0;
        }
        if (jetzt() - start >= maxMs) return { ausgang: "unbekannt" };
        await schlafe(intervall);
    }
}
